import { EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, IPeBuildingConfig, PeInterfaceConfig, IPeRiserConfig, RiserInterfaceMode, BuildingCreateDTO, PlantConfigStep } from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_SELECTED_PLANT_ID } from '@/core/store/action-types';
import { getInterfaceModeLabel, isUniqueLinkingActive } from '@/core/utils/EntrancePlant/entrance-plant.utils';

interface RiserWithInterfaces extends IPeRiserConfig {
  interfaces: PeInterfaceConfig[];
  isBackbone?: boolean;
}

interface BuildingConfig extends IPeBuildingConfig {
  linkedRisersConfig: RiserWithInterfaces[];
  linkedRisersIds: string[];
}

@Options({
  name: 'DialogEntrancePlantCreateBuildingsRecap',
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {},
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateBuildingsRecap extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;

  backboneAndRisersWithInterfaces: RiserWithInterfaces[] = [];
  buildingConfigs: BuildingConfig[] = [];

  getInterfaceModeLabel = getInterfaceModeLabel;

  get pageTitle(): string {
    return this.$t('AppResourceEP.LinkRisersBuildings');
  }

  get totalBuildings(): number {
    return this.buildingConfigs.length;
  }

  get hasBackboneApartments(): boolean {
    return this.backboneApartmentsCount > 0;
  }

  get backboneApartmentsCount(): number {
    return this.structureCreate.pePlantConfig?.numbersOfBackboneApartments ?? 0;
  }

  get isNextDisabled(): boolean {
    // All buildings should have linked risers
    return this.buildingConfigs.some(building => 
      Object.keys(building.linkedRisersConfig || {}).length === 0
    );
  }

  mounted(): void {
    this.initializeRiserWithInterfaces();
    this.initializeBuildingConfigs();
  }

  initializeRiserWithInterfaces(): void {
    const interfaces = this.structureCreate.pePlantConfig?.pePlantConfig?.interfaces || [];
    this.backboneAndRisersWithInterfaces = (this.structureCreate.pePlantConfig?.pePlantConfig?.risers || []).map(riser => {
      const linkedInterfaces = interfaces.filter(intf => intf.peRiserConfigId === riser.id);
      return {
        ...riser,
        interfaces: linkedInterfaces,
      };
    });

    if (this.hasBackboneApartments) {
      const backboneInterface = interfaces.find(intf => intf.isBackboneInterface);
      if (backboneInterface) {
        this.backboneAndRisersWithInterfaces.unshift({
          index: -1,
          id: backboneInterface.peRiserConfigId,
          name: backboneInterface.name,
          numberOfApartments: backboneInterface.numberOfApartments,
          interfaces:[backboneInterface],
          isBackbone: true,
        });
      }
    }
  }

  initializeBuildingConfigs(): void {
    if (this.structureCreate.pePlantConfig?.pePlantConfig?.buildings && this.structureCreate.pePlantConfig.pePlantConfig.buildings.length > 0) {
      const interfaces = this.structureCreate.pePlantConfig?.pePlantConfig?.interfaces || [];
      const backboneInterface = interfaces.find(intf => intf.isBackboneInterface);
      
      this.buildingConfigs = this.structureCreate.pePlantConfig.pePlantConfig.buildings.map(building => {
        // Get linked risers from risers that have this building as peBuildingConfigId
        const linkedRisersIds = this.structureCreate.pePlantConfig?.pePlantConfig?.risers?.filter(riser => {
          return building.id === riser.peBuildingConfigId;
        }).map(riser => riser.id!) || [];
        
        // Check if backbone interface is linked to this building
        if (backboneInterface && backboneInterface.peBuildingConfigId === building.id) {
          // Add backbone interface id (empty GUID) to linked risers
          linkedRisersIds.unshift(backboneInterface.peRiserConfigId!);
        }
        
        const linkedRisersConfig = this.backboneAndRisersWithInterfaces.filter(riser => linkedRisersIds.includes(riser.id!));
        return {
          ...building,
          linkedRisersConfig: linkedRisersConfig,
          linkedRisersIds: linkedRisersIds,
        } as BuildingConfig;
      });
    }
  }

  subtitleComboModeLabel(length: number): string {
    let interfacesComboMode = '';
    if (length == 1) {
      this.$t('AppResourceEP.ShortComboMode5IndependentRiser');
    } else if (length == 2) {
      interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndOneMode7ExtendedRiserExpansion');
    } else if (length > 2) {
      interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion');
    }
    return interfacesComboMode;
  }

  handleBackButton(): void {
    this.$emit('onBack', 'DialogEntrancePlantCreateSetupAddBuildings');
  }

  async handleNextButton() {
    await this.updatePlantStructure();
    this.goToPlantDetail();
  }

  goToPlantDetail() {
    this.$nextTick(async () => {
      await this.$store.dispatch(SET_SELECTED_PLANT_ID, this.plantId);
      this.$router.push({
        name: 'page-entranceplant-dashboard',
        params: { plantId: this.plantId.toString() ?? '' },
      });
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResource.PlantCreated'),
        closable: false,
        life: 3000,
      });
      const summary = this.$t('general.synced');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: summary,
          closable: false,
          life: 3000,
        });
      }, 1500);
    });
  }

  getDataToSave(): EntrancePlantStructureCreateDTO {
    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.END_CONFIG;
    return new EntrancePlantStructureCreateDTO({
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  private async updatePlantStructure(): Promise<void> {
    if (!this.plant) return;

    this.$emit('is-loading', true);

    try {
      const structure = this.getDataToSave();
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.structureCreate.buildingsCreate = result.buildingsCreate;
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}
